import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  X,
  Mic,
  Bot,
  Settings,
  Volume2,
  User,
  Filter,
  Download,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { TranscriptSegment, SpeakerType } from '../../types';

interface LiveTranscriptProps {
  segments: TranscriptSegment[];
  className?: string;
  autoScroll?: boolean;
}

const speakerConfig: Record<
  SpeakerType,
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  customer: {
    label: 'Customer',
    color: 'text-info',
    bg: 'bg-info/10',
    icon: <User className="w-3.5 h-3.5" />,
  },
  ai: {
    label: 'AI Agent',
    color: 'text-vokentra',
    bg: 'bg-vokentra/10',
    icon: <Bot className="w-3.5 h-3.5" />,
  },
  system: {
    label: 'System',
    color: 'text-warning',
    bg: 'bg-warning/10',
    icon: <Settings className="w-3.5 h-3.5" />,
  },
};

function TranscriptItem({ segment, index }: { segment: TranscriptSegment; index: number }) {
  const config = speakerConfig[segment.speaker];
  const isPartial = segment.isPartial;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, x: -10 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{ delay: isPartial ? 0 : index * 0.03, duration: 0.3 }}
      className={cn(
        'group flex gap-3 p-2.5 rounded-xl transition-colors hover:bg-white/[0.02]',
        isPartial && 'bg-white/[0.02]'
      )}
    >
      {/* Speaker avatar */}
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div
          className={cn(
            'w-7 h-7 rounded-lg flex items-center justify-center',
            config.bg,
            config.color
          )}
        >
          {config.icon}
        </div>
        <span className={cn('text-[9px] font-medium', config.color)}>
          {config.label}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] text-text-muted font-mono">
            {new Date(segment.timestamp).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
          {segment.confidence > 0 && (
            <div className="flex items-center gap-1">
              <Volume2 className="w-2.5 h-2.5 text-text-muted" />
              <div className="w-8 h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className={cn(
                    'h-full rounded-full',
                    segment.confidence > 0.8
                      ? 'bg-success'
                      : segment.confidence > 0.5
                      ? 'bg-warning'
                      : 'bg-error'
                  )}
                  initial={{ width: 0 }}
                  animate={{ width: `${segment.confidence * 100}%` }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                />
              </div>
              <span className="text-[9px] text-text-muted font-mono">
                {Math.round(segment.confidence * 100)}%
              </span>
            </div>
          )}
          {isPartial && (
            <span className="text-[9px] text-warning animate-pulse">typing...</span>
          )}
        </div>

        <p
          className={cn(
            'text-sm leading-relaxed',
            isPartial ? 'text-text-secondary' : 'text-text-primary',
            segment.confidence < 0.5 && !isPartial && 'opacity-60'
          )}
        >
          {segment.text}
          {isPartial && (
            <motion.span
              className="inline-block w-0.5 h-4 bg-vokentra ml-0.5 align-middle"
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
          )}
        </p>

        {/* Word timings */}
        {segment.wordTimings && segment.wordTimings.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {segment.wordTimings.map((wt, i) => (
              <span
                key={i}
                className={cn(
                  'text-[10px] px-1 py-0.5 rounded font-mono',
                  wt.confidence > 0.9
                    ? 'bg-success/10 text-success/70'
                    : wt.confidence > 0.6
                    ? 'bg-warning/10 text-warning/70'
                    : 'bg-error/10 text-error/70'
                )}
                title={`${wt.start.toFixed(2)}s - ${wt.end.toFixed(2)}s (${Math.round(
                  wt.confidence * 100
                )}%)`}
              >
                {wt.word}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function LiveTranscript({
  segments,
  className,
  autoScroll = true,
}: LiveTranscriptProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [speakerFilter, setSpeakerFilter] = useState<SpeakerType | 'all'>('all');
  const [showSearch, setShowSearch] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevSegmentsLength = useRef(segments.length);

  const filteredSegments = useMemo(() => {
    let result = segments;

    if (speakerFilter !== 'all') {
      result = result.filter((s) => s.speaker === speakerFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((s) => s.text.toLowerCase().includes(query));
    }

    return result;
  }, [segments, speakerFilter, searchQuery]);

  // Auto-scroll to bottom on new segments
  useEffect(() => {
    if (autoScroll && scrollRef.current && segments.length > prevSegmentsLength.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevSegmentsLength.current = segments.length;
  }, [segments.length, autoScroll]);

  const handleExport = () => {
    const text = segments
      .map(
        (s) =>
          `[${new Date(s.timestamp).toLocaleTimeString()}] ${s.speaker.toUpperCase()}: ${s.text}`
      )
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const speakerCounts = {
    all: segments.length,
    customer: segments.filter((s) => s.speaker === 'customer').length,
    ai: segments.filter((s) => s.speaker === 'ai').length,
    system: segments.filter((s) => s.speaker === 'system').length,
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header with search and filters */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-vokentra" />
          <span className="text-sm font-semibold text-text-primary">Live Transcript</span>
          <motion.span
            className="text-xs text-text-muted bg-white/5 px-2 py-0.5 rounded-full"
            key={segments.length}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
          >
            {segments.length}
          </motion.span>
        </div>

        <div className="flex items-center gap-1">
          <motion.button
            onClick={() => setShowSearch(!showSearch)}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              showSearch ? 'bg-vokentra/20 text-vokentra' : 'text-text-muted hover:text-text-primary'
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Search className="w-4 h-4" />
          </motion.button>
          <motion.button
            onClick={handleExport}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Download className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Search bar */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden mb-3"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search transcript..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-9 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-vokentra/50 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Speaker filters */}
      <div className="flex items-center gap-1 mb-3">
        <Filter className="w-3 h-3 text-text-muted mr-1" />
        {(Object.keys(speakerConfig) as SpeakerType[]).map((speaker) => (
          <button
            key={speaker}
            onClick={() => setSpeakerFilter(speakerFilter === speaker ? 'all' : speaker)}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-all',
              speakerFilter === speaker
                ? cn(speakerConfig[speaker].bg, speakerConfig[speaker].color)
                : 'text-text-muted hover:text-text-secondary hover:bg-white/5'
            )}
          >
            {speakerConfig[speaker].icon}
            <span className="capitalize">{speaker}</span>
            <span className="text-[9px] opacity-60">{speakerCounts[speaker]}</span>
          </button>
        ))}
      </div>

      {/* Segments */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto pr-1 space-y-1 min-h-0"
      >
        <AnimatePresence mode="popLayout">
          {filteredSegments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-text-muted text-sm"
            >
              {segments.length === 0
                ? 'No transcript available'
                : 'No matching segments'}
            </motion.div>
          ) : (
            filteredSegments.map((segment, index) => (
              <TranscriptItem key={segment.id} segment={segment} index={index} />
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Compact transcript for call cards
interface CompactTranscriptProps {
  lastSegment?: TranscriptSegment;
  className?: string;
}

export function CompactTranscript({ lastSegment, className }: CompactTranscriptProps) {
  if (!lastSegment) {
    return (
      <div className={cn('text-xs text-text-muted italic', className)}>
        No transcript yet
      </div>
    );
  }

  const config = speakerConfig[lastSegment.speaker];

  return (
    <div className={cn('flex items-start gap-2', className)}>
      <div className={cn('w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5', config.bg, config.color)}>
        {config.icon}
      </div>
      <div className="min-w-0">
        <span className={cn('text-[10px] font-medium', config.color)}>
          {config.label}
        </span>
        <p className="text-xs text-text-secondary truncate">
          {lastSegment.isPartial ? (
            <>
              {lastSegment.text}
              <motion.span
                className="inline-block w-0.5 h-3 bg-vokentra ml-0.5 align-middle"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
            </>
          ) : (
            lastSegment.text
          )}
        </p>
      </div>
    </div>
  );
}
